import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SocialMediaLinksTab } from '@/components/profile/SocialMediaLinksTab';
import {
  User,
  MapPin,
  Calendar,
  Trophy,
  Target,
  Users,
  Star,
  TrendingUp,
  Phone,
  Mail,
  Instagram,
  Twitter,
  Youtube,
  Globe,
  UserCheck,
  Briefcase,
  Edit,
  Link,
  Award,
  GraduationCap,
  Settings,
  Shield,
  CheckCircle2,
  Shirt
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { cn } from '@/lib/utils';
import { useUser } from '@/contexts/UserContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useTalentData } from '@/hooks/useTalentData';
import { ProfileCompletionPrompt } from '@/components/ProfileCompletionPrompt';
import { Skeleton } from '@/components/ui/skeleton';
import TalentProfileEditDrawer from './TalentProfileEditDrawer';
import { userService } from '@/services/userService';
import { LinkAgentModal } from '@/components/athlete/LinkAgentModal';
import { LinkedAgentCard } from '@/components/athlete/LinkedAgentCard';
import { AdminProfileSection } from '@/components/admin/AdminProfileSection';
import { SafeAvatarImage } from '@/components/ui/safe-avatar-image';
import { getProxiedImageUrl } from '@/utils/imageProxy';


const TalentProfile: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, accountType, refreshUserData } = useUser();
  const { isAdmin } = useUserRole();
  const { athleteData, claimedAthlete, claimedCreator, claimedCreators, loading, error } = useTalentData();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editSection, setEditSection] = useState<'overview' | 'athletic' | 'personal' | 'contact'>('overview');

  if (!currentUser) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Please log in to view your profile.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Skeleton className="h-20 w-20 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (error || !athleteData) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center space-y-4">
            <p className="text-muted-foreground">
              {error || "Failed to load athlete profile data."}
            </p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const athlete = athleteData?.talent;
  const talentType = athlete?.talent_type || 'other';
  const isAthlete = talentType === 'athlete';

  // Check stats JSON for musician-specific fields to identify musicians stored as 'other'
  const statsData = athlete?.stats as Record<string, any> | null;
  const isMusicianLike = !isAthlete && statsData?.music_genre;

  const getTalentTypeLabel = () => {
    if (isMusicianLike) return 'Musician';
    switch (talentType) {
      case 'athlete': return athlete?.sport ? `${athlete.sport} Athlete` : 'Professional Athlete';
      case 'creator': return 'Creator / Influencer';
      case 'entertainer': return 'Entertainer / Actor';
      default: return 'Talent';
    }
  };

  const getTalentTypeBadge = () => {
    if (isMusicianLike) return 'Musician';
    switch (talentType) {
      case 'athlete': return 'Athlete';
      case 'creator': return 'Creator';
      case 'entertainer': return 'Entertainer';
      default: return 'Talent';
    }
  };

  const handleEditClick = (section: 'overview' | 'athletic' | 'personal' | 'contact') => {
    setEditSection(section);
    setEditDrawerOpen(true);
  };

  const handleSave = async (updatedData: any) => {
    try {
      await userService.updateProfile(currentUser.id, updatedData);
      await refreshUserData();
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <ProfileCompletionPrompt />
      
      {/* Admin Section */}
      {isAdmin && <AdminProfileSection />}
      
      {/* Header Card */}
      <Card>
        <CardHeader className="pb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative">
              <Avatar className="h-20 w-20 border-2 border-primary/20">
                <AvatarImage src={claimedAthlete?.photo_url || currentUser.avatar_url || athlete?.photo} />
                <AvatarFallback className="text-lg">
                  {(claimedAthlete?.name || athlete?.name || currentUser.display_name)?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {claimedAthlete?.is_verified && (
                <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center border-2 border-background">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
              )}
              <Button
                variant="outline"
                size="icon"
                className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full shadow-md bg-background"
                onClick={() => navigate('/settings')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-foreground">
                      {claimedAthlete?.name || athlete?.name || currentUser.display_name}
                    </h1>
                    {isAthlete && claimedAthlete?.jersey_number && (
                      <Badge variant="outline" className="text-base font-bold">
                        <Shirt className="h-4 w-4 mr-1" />
                        #{claimedAthlete.jersey_number}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-muted-foreground">
                      {isAthlete 
                        ? (claimedAthlete?.sport || athlete?.sport ? `${claimedAthlete?.sport || athlete.sport} Athlete` : 'Professional Athlete')
                        : getTalentTypeLabel()}
                    </p>
                    {claimedAthlete?.position && (
                      <Badge variant="secondary" className="text-xs">
                        {claimedAthlete.position}
                      </Badge>
                    )}
                  </div>
                  {isAthlete && (claimedAthlete?.current_team_name || athlete?.team_name) && (
                    <div className="flex items-center gap-2 mt-1">
                      {claimedAthlete?.current_team_logo && (
                        <img 
                          src={claimedAthlete.current_team_logo} 
                          alt={claimedAthlete.current_team_name}
                          className="h-5 w-5 object-contain"
                        />
                      )}
                      <p className="text-sm text-muted-foreground font-medium">
                        {claimedAthlete?.current_team_name || athlete?.team_name}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {(currentUser.is_verified || claimedAthlete?.is_verified) && (
                    <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                  <Badge variant="outline">
                    {getTalentTypeBadge()}
                  </Badge>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
                {(currentUser.location || claimedAthlete?.nationality || athlete?.nationality) && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {currentUser.location || claimedAthlete?.nationality || athlete?.nationality}
                  </div>
                )}
                {claimedAthlete?.age && (
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {claimedAthlete.age} years old
                  </div>
                )}
                {isAthlete && (claimedAthlete?.height || claimedAthlete?.weight) && (
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    {claimedAthlete.height && `${claimedAthlete.height}`}
                    {claimedAthlete.height && claimedAthlete.weight && ' • '}
                    {claimedAthlete.weight && `${claimedAthlete.weight}`}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Joined {new Date(currentUser.created_at).toLocaleDateString()}
                </div>
                {athlete?.tier && (
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4" />
                    {athlete.tier} Tier
                  </div>
                )}
              </div>

              {/* Claim Profile CTA — primary call-to-action with substantial
                  visual weight so it doesn't look orphaned in the wide header
                  card. size="lg" + min-width gives the button presence at
                  desktop widths instead of shrinking to its content (sm:w-auto
                  alone made it look tiny). */}
              {!currentUser.is_verified && !claimedAthlete && !claimedCreator && (
                <div className="mt-4">
                  {athleteData?.talent?.talent_type === 'athlete' ? (
                    <Button
                      size="lg"
                      className="w-full sm:w-auto sm:min-w-[320px]"
                      onClick={() => navigate('/claim-athlete')}
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Claim Your Verified Athlete Profile
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      className="w-full sm:w-auto sm:min-w-[320px]"
                      onClick={() => navigate('/claim-creator')}
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Claim Your Creator Profile
                    </Button>
                  )}
                </div>
              )}

              {(currentUser.bio || (athlete?.achievements && athlete.achievements.length > 0)) && (
                <div className="mt-4 space-y-2">
                  {currentUser.bio && (
                    <p className="text-foreground leading-relaxed">
                      {currentUser.bio}
                    </p>
                  )}
                  {athlete?.achievements && athlete.achievements.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {athlete.achievements.slice(0, 3).map((achievement, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          <Trophy className="h-3 w-3 mr-1" />
                          {achievement}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabbed Profile Content */}
      {/* The Career Stats tab only renders when claimedAthlete exists, so the
          column count must match what's actually rendered — otherwise the grid
          leaves an empty column at the end and tabs look sparsely spaced. */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className={cn(
          "flex w-full overflow-x-auto md:grid",
          claimedAthlete ? "md:grid-cols-6" : "md:grid-cols-5"
        )}>
          <TabsTrigger value="overview" className="whitespace-nowrap text-xs md:text-sm">Overview</TabsTrigger>
          {claimedAthlete && (
            <TabsTrigger value="stats" className="whitespace-nowrap text-xs md:text-sm">Career Stats</TabsTrigger>
          )}
          <TabsTrigger value="athletic" className="whitespace-nowrap text-xs md:text-sm">{isAthlete ? 'Athletic Details' : 'Talent Details'}</TabsTrigger>
          <TabsTrigger value="personal" className="whitespace-nowrap text-xs md:text-sm">Personal Info</TabsTrigger>
          <TabsTrigger value="contact" className="whitespace-nowrap text-xs md:text-sm">Contact Info</TabsTrigger>
          <TabsTrigger value="social" className="whitespace-nowrap text-xs md:text-sm">Social Media</TabsTrigger>
        </TabsList>

        {/* Career Stats Tab */}
        {claimedAthlete && (
          <TabsContent value="stats" className="space-y-4">
            {claimedAthlete.career_stats && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-primary" />
                    Career Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {(() => {
                    const stats = claimedAthlete.career_stats;
                    
                    // Convert stats to chart data
                    const statsData = Object.entries(stats)
                      .filter(([key, value]) => typeof value === 'number' && key !== 'games')
                      .map(([key, value]) => ({
                        name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        value: value as number
                      }))
                      .slice(0, 6);

                    return statsData.length > 0 ? (
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={statsData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis 
                              dataKey="name" 
                              stroke="hsl(var(--muted-foreground))"
                              tick={{ fill: 'hsl(var(--muted-foreground))' }}
                            />
                            <YAxis 
                              stroke="hsl(var(--muted-foreground))"
                              tick={{ fill: 'hsl(var(--muted-foreground))' }}
                            />
                            <Tooltip 
                              contentStyle={{
                                backgroundColor: 'hsl(var(--background))',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '0.5rem'
                              }}
                            />
                            <Bar dataKey="value" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">No stats data available</p>
                    );
                  })()}
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {Object.entries(claimedAthlete.career_stats)
                      .filter(([_, value]) => typeof value === 'number')
                      .map(([key, value]) => (
                        <div key={key} className="bg-muted/50 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-foreground">
                            {(value as number).toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {claimedAthlete.current_season_stats && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Current Season Stats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(claimedAthlete.current_season_stats)
                      .filter(([_, value]) => typeof value === 'number')
                      .map(([key, value]) => (
                        <div key={key} className="bg-accent/50 rounded-lg p-4 text-center border border-primary/20">
                          <div className="text-2xl font-bold text-primary">
                            {(value as number).toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Profile Overview
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('overview')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Mobile: stack label/value vertically so long values don't crash into the label.
                  Desktop (sm+): original side-by-side layout. */}
              <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center">
                <span className="text-sm text-muted-foreground">Full Name</span>
                <span className="font-medium">
                  {claimedAthlete?.first_name && claimedAthlete?.last_name
                    ? `${claimedAthlete.first_name} ${claimedAthlete.last_name}`
                    : claimedAthlete?.name || (athlete?.first_name && athlete?.last_name
                      ? `${athlete.first_name} ${athlete.last_name}`
                      : athlete?.name || currentUser.display_name)
                  }
                </span>
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center">
                <span className="text-sm text-muted-foreground">Age</span>
                <span className="font-medium">
                  {claimedAthlete?.age
                    ? claimedAthlete.age
                    : claimedAthlete?.birth_date
                      ? new Date().getFullYear() - new Date(claimedAthlete.birth_date).getFullYear()
                      : athlete?.date_of_birth
                        ? new Date().getFullYear() - new Date(athlete.date_of_birth).getFullYear()
                        : 'Not specified'
                  }
                </span>
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-center">
                <span className="text-sm text-muted-foreground">Position</span>
                <span className="font-medium">{claimedAthlete?.position || athlete?.position || 'Not specified'}</span>
              </div>
              <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-start">
                <span className="text-sm text-muted-foreground sm:flex-shrink-0">Bio</span>
                <span className="font-medium sm:text-right sm:max-w-xs sm:truncate">{claimedAthlete?.espn_bio || currentUser.bio || 'Not provided'}</span>
              </div>
            </CardContent>
          </Card>

          {/* Linked Creator Accounts — shows every approved claim from the
              /claim-creator flow. Gives talent users a single place to see
              which platforms they've verified ownership of. */}
          {claimedCreators && claimedCreators.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Link className="h-5 w-5 text-primary" />
                    Linked Creator Accounts
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/claim-creator')}
                  >
                    Manage
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {claimedCreators.map((creator) => {
                  const formatFollowers = (n: number | null | undefined) => {
                    if (!n || n <= 0) return null;
                    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
                    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
                    return String(n);
                  };
                  const followers = formatFollowers(creator.follower_count);
                  const PlatformIcon =
                    creator.platform === 'instagram' ? Instagram :
                    creator.platform === 'youtube' ? Youtube :
                    null;
                  return (
                    <div key={creator.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border/40">
                      <Avatar className="h-10 w-10 shrink-0">
                        <SafeAvatarImage
                          src={getProxiedImageUrl(creator.avatar_url || '')}
                          className="object-cover"
                        />
                        <AvatarFallback>
                          {(creator.display_name || creator.username)?.[0]?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">
                            {creator.display_name || creator.username}
                          </span>
                          {creator.is_verified && (
                            <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                          )}
                          {creator.primary_platform && (
                            <Badge variant="secondary" className="text-xs">Primary</Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                          {PlatformIcon && <PlatformIcon className="h-3 w-3" />}
                          <span className="truncate">@{creator.username}</span>
                          <span>•</span>
                          <span className="capitalize">{creator.platform}</span>
                          {followers && (
                            <>
                              <span>•</span>
                              <span>{followers} followers</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="athletic" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-primary" />
                  Athletic Information
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('athletic')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Sport</span>
                  <span className="font-medium">{claimedAthlete?.sport || athlete?.sport || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Position</span>
                  <span className="font-medium">{claimedAthlete?.position || athlete?.position || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Team/Club</span>
                  <span className="font-medium">{claimedAthlete?.current_team_name || athlete?.team_name || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Club Abbreviation</span>
                  <span className="font-medium">{athlete?.club_abbreviation || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Competition Level</span>
                  <span className="font-medium">{athlete?.competition_level || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Height</span>
                  <span className="font-medium">{claimedAthlete?.height || athlete?.height || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Weight</span>
                  <span className="font-medium">{claimedAthlete?.weight || athlete?.weight || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Nationality</span>
                  <span className="font-medium">{claimedAthlete?.nationality || athlete?.nationality || 'Not specified'}</span>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground">Career Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Affiliate Organization</span>
                    <span className="font-medium">{athlete?.affiliate_organization || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Affiliate State</span>
                    <span className="font-medium">{athlete?.affiliate_state || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Draft Round</span>
                    <span className="font-medium">{claimedAthlete?.draft_round || athlete?.draft_round || 'Not drafted'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Year Drafted</span>
                    <span className="font-medium">{claimedAthlete?.draft_year || athlete?.draft_year || 'Not drafted'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      College/University
                    </span>
                    <span className="font-medium">{claimedAthlete?.college || athlete?.college_university || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Coach</span>
                    <span className="font-medium">{athlete?.coach_name || 'Not specified'}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="personal" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Personal Information
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('personal')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">First Name</span>
                  <span className="font-medium">{claimedAthlete?.first_name || athlete?.first_name || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Last Name</span>
                  <span className="font-medium">{claimedAthlete?.last_name || athlete?.last_name || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Date of Birth
                  </span>
                  <span className="font-medium">
                    {claimedAthlete?.birth_date 
                      ? new Date(claimedAthlete.birth_date).toLocaleDateString()
                      : athlete?.date_of_birth 
                        ? new Date(athlete.date_of_birth).toLocaleDateString()
                        : 'Not specified'
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Age</span>
                  <span className="font-medium">
                    {claimedAthlete?.age 
                      ? claimedAthlete.age
                      : claimedAthlete?.birth_date
                        ? new Date().getFullYear() - new Date(claimedAthlete.birth_date).getFullYear()
                        : athlete?.date_of_birth 
                          ? new Date().getFullYear() - new Date(athlete.date_of_birth).getFullYear()
                          : 'Not specified'
                    }
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-muted-foreground">Address Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:items-start">
                    <span className="text-sm text-muted-foreground sm:flex-shrink-0">Street Address</span>
                    <span className="font-medium sm:text-right sm:max-w-xs sm:truncate">{athlete?.street_address || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">City</span>
                    <span className="font-medium">{athlete?.city || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">State</span>
                    <span className="font-medium">{athlete?.state || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Zip Code</span>
                    <span className="font-medium">{athlete?.zip_code || 'Not specified'}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        <TabsContent value="contact" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Contact Information
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEditClick('contact')}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </span>
                  <span className="font-medium">{currentUser.email}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Personal Email
                  </span>
                  <span className="font-medium">{athlete?.personal_email || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Cell Phone
                  </span>
                  <span className="font-medium">{athlete?.cell_phone || 'Not specified'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                    <Instagram className="h-4 w-4" />
                    Instagram
                  </span>
                  <span className="font-medium">
                    {athlete?.instagram_handle 
                      ? `@${athlete.instagram_handle}`
                      : 'Not specified'
                    }
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Account Type</span>
                  <Badge variant="outline">{accountType}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Profile Status</span>
                  <Badge variant={currentUser.setup_completed ? "default" : "secondary"}>
                    {currentUser.setup_completed ? 'Complete' : 'Incomplete'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Agent Connection Section */}
          {athlete?.agent_id ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  Agent Connection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Connected to agent
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  Agent Connection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Connect with your agent to allow them to view your performance data and help manage your career.
                  </p>
                  <LinkAgentModal onLinked={() => window.location.reload()}>
                    <Button variant="outline" className="w-full">
                      <Link className="h-4 w-4 mr-2" />
                      Link to Agent
                    </Button>
                  </LinkAgentModal>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="social" className="space-y-4">
          <SocialMediaLinksTab />
        </TabsContent>
      </Tabs>

      <TalentProfileEditDrawer
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        section={editSection}
        data={athleteData}
        onSave={handleSave}
      />
    </div>
  );
};

export default TalentProfile;